import _ from 'lodash';

/**
 * The image editor drawer (GenerateImageEditDrawer) on a product form: opened
 * from a ProductMedium tile's "Edit" button, its events write back into the
 * product form's media so the next product Save persists them.
 *
 * Shared by the product form (Main.vue) and every other surface that shows
 * the product's media tiles (tenant apps). The host provides
 * `shopProductStore`, `uploadEndpoint` and `onGenerationCompleted()`
 * (refetch the product).
 */
export default {
    data: () => ({
        showImageDrawer: false,
        imageEditSourceId: null,
    }),
    computed: {
        // Lets the image editor's own Replace/Upload control post to the product
        // upload endpoint with the product media context.
        imageEditorUploadContext() {
            return { endpoint: this.uploadEndpoint, type: 'product' };
        },
    },
    methods: {
        // Opened from a specific image's "Edit" button: the unified Edit drawer
        // (crop + AI + layer stack) references that image directly.
        openEditImage(imageId) {
            this.imageEditSourceId = imageId;
            this.showImageDrawer = true;
        },
        editedMedia(imageId) {
            return (_.get(this.shopProductStore.form, 'media') || []).find(
                (item) => String(_.get(item, 'image.id')) === String(imageId)
            );
        },
        // The editor emitted an updated image. For an UNSAVED upload (client `new:` id,
        // possibly promoted to a real id via `previousId`), merge the change into the
        // form media in place: a refetch would wipe the unsaved upload. For a persisted
        // image, refetch to pick up the server-side mutation.
        onImageUpdated(image, previousId = null) {
            if (!image) return;
            const matchId = previousId != null ? previousId : _.get(image, 'id');
            const media = this.editedMedia(matchId);
            const wasUnsaved =
                String(matchId || '').startsWith('new:') || String(_.get(image, 'id') || '').startsWith('new:');
            if (media && media.image && wasUnsaved) {
                const keys = [
                    'id',
                    'source',
                    'settings',
                    'processing_query_string',
                    'processedFile',
                    'activeVariant',
                    'variants',
                    'alt',
                    'width',
                    'height',
                    'url',
                    'upload',
                    'ai_disclosure',
                    'ai_source_type',
                    'ai_label_display',
                    'ai_assessment',
                ];
                keys.forEach((key) => this.$set(media.image, key, _.get(image, key)));
                return;
            }
            this.onGenerationCompleted();
        },
        // The editor replaced the image (uploaded a new file or picked one from the
        // library). Swap the whole image object in the matching media slot so the
        // product Save persists the new source.
        onImageReplaced(image, previousId) {
            if (!image) return;
            const media = this.editedMedia(previousId);
            if (media) {
                this.$set(media, 'image', image);
            }
        },
        // Alt text and the Art. 50(4) disclosure decisions ride the product
        // media payload, so the next product Save persists them.
        onImageAltUpdated(imageId, alt) {
            const media = this.editedMedia(imageId);
            if (media && media.image) {
                this.$set(media.image, 'alt', alt);
            }
        },
        onImageAiDisclosureUpdated(imageId, disclose) {
            const media = this.editedMedia(imageId);
            if (media && media.image) {
                this.$set(media.image, 'ai_disclosure', disclose === true);
            }
        },
        onImageAiLabelDisplayUpdated(imageId, mode) {
            const media = this.editedMedia(imageId);
            if (media && media.image) {
                this.$set(media.image, 'ai_label_display', mode || null);
            }
        },
        onImageAiSourceTypeUpdated(imageId, sourceType) {
            const media = this.editedMedia(imageId);
            if (media && media.image) {
                this.$set(media.image, 'ai_source_type', sourceType || null);
            }
        },
    },
};
